/**
 * Rebuy Smart Cart drawer enhancements (Figma 491:2437 / 491:6716).
 * Does not touch .rebuy-cart__flyout-recommendations internals.
 */
class RebuyCartEnhancer extends HTMLElement {
  connectedCallback() {
    if (this._started) return;
    this._started = true;

    this.assets = {
      eye: this.dataset.eye,
      flower: this.dataset.flower,
    };

    // Rebuy renders client-side, so every string this file writes comes in
    // pre-translated on the element rather than being spelled out here.
    this.strings = {
      subtotal: this.dataset.subtotal,
      taxesNote: this.dataset.taxesNote,
      itemCountOne: this.dataset.itemCountOne,
      itemCountOther: this.dataset.itemCountOther,
    };

    this._onSchedule = this.schedule.bind(this);
    this._scheduled = false;

    this._onRemoveClick = this.onRemoveClick.bind(this);
    document.addEventListener('click', this._onRemoveClick, true);

    this.enhance();

    this._observer = new MutationObserver(this._onSchedule);
    this._observer.observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener('rebuy.smartcart.show', this._onSchedule);
    document.addEventListener('rebuy.smartcart.ready', this._onSchedule);
    document.addEventListener('rebuy.smartcart.change', this._onSchedule);
    document.addEventListener('rebuy:cart.change', this._onSchedule);
    window.addEventListener('rebuy.cart.change', this._onSchedule);
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    document.removeEventListener('click', this._onRemoveClick, true);
    document.removeEventListener('rebuy.smartcart.show', this._onSchedule);
    document.removeEventListener('rebuy.smartcart.ready', this._onSchedule);
    document.removeEventListener('rebuy.smartcart.change', this._onSchedule);
    document.removeEventListener('rebuy:cart.change', this._onSchedule);
    window.removeEventListener('rebuy.cart.change', this._onSchedule);
  }

  onRemoveClick(event) {
    var btn = event.target.closest && event.target.closest('.rebuy-cart__flyout-item-remove');
    if (!btn || btn.closest('.rebuy-cart__flyout-recommendations')) return;
    btn.classList.add('is-removing');
    btn.setAttribute('aria-busy', 'true');
  }

  flyout() {
    return document.querySelector('.rebuy-cart__flyout');
  }

  smartCart() {
    return window.SmartCart || (window.Rebuy && window.Rebuy.SmartCart) || null;
  }

  itemCount() {
    var cart = this.smartCart();
    if (cart && typeof cart.itemCount === 'function') {
      return Number(cart.itemCount()) || 0;
    }
    if (typeof window.SmartCart === 'object' && typeof window.SmartCart.itemCount === 'function') {
      return Number(window.SmartCart.itemCount()) || 0;
    }
    var total = 0;
    document.querySelectorAll('.rebuy-cart__flyout-item-quantity-widget-label').forEach(function (el) {
      var n = parseInt(el.textContent.replace(/[^\d]/g, ''), 10);
      if (!isNaN(n)) total += n;
    });
    return total;
  }

  subtotalText() {
    var box = document.querySelector('.rebuy-cart__flyout-subtotal-amount');
    if (!box) return '';

    var clone = box.cloneNode(true);
    clone.querySelectorAll('.sr-only, [aria-hidden="true"], s, del, .compare-at, .rebuy-money--compare').forEach(function (el) {
      el.parentNode.removeChild(el);
    });

    var money = clone.querySelector('.rebuy-money, .money');
    var source = money ? money.textContent : clone.textContent;
    var match = String(source)
      .replace(/Final price|Original price|Compare at/gi, ' ')
      .match(/\$[\d,]+(?:\.\d{2})?/);
    return match ? match[0] : '';
  }

  ensureWave(root) {
    if (!root.querySelector('.fs-rebuy-cart-wave--desktop')) {
      var wave = document.createElement('div');
      wave.className = 'fs-rebuy-cart-wave fs-rebuy-cart-wave--desktop';
      wave.setAttribute('aria-hidden', 'true');
      root.insertBefore(wave, root.firstChild);
    }

    if (!root.querySelector('.fs-rebuy-cart-wave--mobile')) {
      var waveMobile = document.createElement('div');
      waveMobile.className = 'fs-rebuy-cart-wave fs-rebuy-cart-wave--mobile';
      waveMobile.setAttribute('aria-hidden', 'true');
      root.insertBefore(waveMobile, root.firstChild);
    }
  }

  ensureHeader(root) {
    var header = root.querySelector('.rebuy-cart__flyout-header');
    var titleWrap = header && header.querySelector('div');
    var title = root.querySelector('.rebuy-cart__flyout-title');
    if (!header || !titleWrap || !title) return;

    if (this.assets.eye && !titleWrap.querySelector('.fs-rebuy-cart-eye')) {
      var eye = document.createElement('img');
      eye.className = 'fs-rebuy-cart-eye';
      eye.src = this.assets.eye;
      eye.alt = '';
      eye.setAttribute('aria-hidden', 'true');
      titleWrap.insertBefore(eye, title);
    }

    if (!titleWrap.querySelector('.fs-rebuy-cart-count')) {
      var count = document.createElement('span');
      count.className = 'fs-rebuy-cart-count';
      title.insertAdjacentElement('afterend', count);
    }
  }

  updateCount(root) {
    var el = root.querySelector('.fs-rebuy-cart-count');
    if (!el) return;
    var n = this.itemCount();
    var template = n === 1 ? this.strings.itemCountOne : this.strings.itemCountOther;
    el.textContent = template ? template.replace('__count__', n) : '';
  }

  cartIsEmpty() {
    var cart = this.smartCart();
    if (cart && typeof cart.hasItems === 'function') {
      return !cart.hasItems();
    }
    return this.itemCount() === 0;
  }

  syncEmptyState(root) {
    var empty = this.cartIsEmpty() || !!root.querySelector('.rebuy-cart__flyout-content.no-items');
    root.classList.toggle('fs-rebuy-cart-is-empty', empty);
  }

  // Rebuy exposes reviews on its recommendation widgets but not on cart line
  // items, so the rating row comes from the theme's own product view — the same
  // Okendo metafields the collection cards read. One request per handle, cached
  // for the life of the page because Rebuy re-renders its items constantly.
  reviewsFor(handle) {
    if (!this._reviewCache) this._reviewCache = {};
    if (this._reviewCache[handle]) return this._reviewCache[handle];

    var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    this._reviewCache[handle] = fetch(routeRoot + 'products/' + encodeURIComponent(handle) + '?view=cart-reviews')
      .then(function (response) {
        return response.ok ? response.text() : '';
      })
      .catch(function () {
        return '';
      });

    return this._reviewCache[handle];
  }

  ensureItemReviews(root) {
    var self = this;

    root.querySelectorAll('.rebuy-cart__flyout-item').forEach(function (item) {
      // Recommendation items are Rebuy's own; this file leaves them alone.
      if (item.closest('.rebuy-cart__flyout-recommendations')) return;

      var info = item.querySelector('.rebuy-cart__flyout-item-info');
      if (!info) return;

      var link = item.querySelector('a[href*="/products/"]');
      var match = link && link.getAttribute('href').match(/\/products\/([^/?#]+)/);
      if (!match) return;

      var handle = match[1];
      var existing = info.querySelector('.fs-rebuy-item-reviews');
      if (existing && existing.dataset.handle === handle) return;
      if (existing) existing.parentNode.removeChild(existing);

      // The slot claims its place before the fetch resolves, so a re-render
      // mid-flight cannot queue a second row for the same item.
      var slot = document.createElement('div');
      slot.className = 'fs-rebuy-item-reviews';
      slot.dataset.handle = handle;

      var title = info.querySelector('.rebuy-cart__flyout-item-product-title');
      if (title) {
        title.insertAdjacentElement('afterend', slot);
      } else {
        info.insertBefore(slot, info.firstChild);
      }

      self.reviewsFor(handle).then(function (html) {
        if (!slot.isConnected || slot.dataset.handle !== handle) return;
        slot.innerHTML = html;
      });
    });
  }

  ensureProgress(root) {
    var meter = root.querySelector('.rebuy-cart__progress-bar-meter');
    var wrapper = root.querySelector('.rebuy-cart__progress-bar-wrapper');
    if (meter && this.assets.flower && !meter.querySelector('.fs-rebuy-progress-flower')) {
      var flower = document.createElement('img');
      flower.className = 'fs-rebuy-progress-flower';
      flower.src = this.assets.flower;
      flower.alt = '';
      flower.setAttribute('aria-hidden', 'true');
      meter.appendChild(flower);
    }

    if (wrapper && !wrapper.querySelector('.fs-rebuy-progress-total')) {
      var total = document.createElement('div');
      total.className = 'fs-rebuy-progress-total';
      wrapper.appendChild(total);
    }

    var prompt = root.querySelector('.rebuy-cart__progress-bar-prompt');
    if (prompt) this.normalizePrompt(prompt);
  }

  normalizePrompt(prompt) {
    var html = prompt.innerHTML;
    html = html.replace(/You are/gi, "You're");
    html = html.replace(/You have unlocked/gi, "You've unlocked");
    html = html.replace(/You've unlock(?!ed)/gi, "You've unlocked");
    html = html.replace(/\s*Congratulations!?\.?\s*/gi, ' ');
    if (!/<b>|<strong>/i.test(html) && /FREE\s*SHIPPING/i.test(html)) {
      html = html.replace(/FREE\s*SHIPPING/i, '<b>FREE SHIPPING</b>');
    }
    if (html !== prompt.innerHTML) prompt.innerHTML = html;
  }

  updateProgress(root) {
    var fill = root.querySelector('.rebuy-cart__progress-bar-meter-fill');
    var flower = root.querySelector('.fs-rebuy-progress-flower');
    if (fill && flower) {
      var width = fill.style.width || getComputedStyle(fill).width;
      flower.style.left = width;
    }

    var total = root.querySelector('.fs-rebuy-progress-total');
    if (total) total.textContent = this.subtotalText();
  }

  cleanGiftNote(root) {
    root.querySelectorAll('.rebuy-cart__flyout-integration').forEach(function (box) {
      var nodes = [box].concat(Array.prototype.slice.call(box.querySelectorAll('*')));
      nodes.forEach(function (el) {
        Array.prototype.slice.call(el.childNodes).forEach(function (node) {
          if (node.nodeType !== 3) return;
          if (/rebuy-cart__flyout|!important/.test(node.textContent)) {
            node.parentNode.removeChild(node);
          }
        });
      });
    });

    var giftStyle = document.querySelector('#giftnote-rebuycart style');
    if (giftStyle && !giftStyle.getAttribute('data-fs-gift')) {
      giftStyle.textContent =
        '#button{' +
        'font-family:Jost,sans-serif!important;' +
        '-webkit-appearance:none;' +
        'appearance:none;' +
        'display:inline-block;' +
        'width:auto;' +
        'max-width:100%;' +
        'margin:0;' +
        'padding:0;' +
        'border:0!important;' +
        'border-radius:0;' +
        'background:transparent!important;' +
        'box-shadow:none!important;' +
        'color:#000!important;' +
        'font-weight:600!important;' +
        'font-size:14px!important;' +
        'letter-spacing:.3px;' +
        'line-height:1.5;' +
        'text-align:left;' +
        'text-transform:uppercase;' +
        'cursor:pointer;' +
        '}' +
        '#button:hover{opacity:.7;}';
      giftStyle.setAttribute('data-fs-gift', 'true');
    }
  }

  moveGiftNote(root) {
    var gift = root.querySelector('.rebuy-cart__flyout-footer .rebuy-cart__flyout-integration, .rebuy-cart__flyout-integration');
    var recs = root.querySelector('.rebuy-cart__flyout-recommendations');
    var content = root.querySelector('.rebuy-cart__flyout-content');
    if (!gift || !content) return;
    if (recs && gift.nextElementSibling !== recs) {
      content.insertBefore(gift, recs);
    } else if (!recs && gift.parentElement !== content) {
      content.appendChild(gift);
    }
  }

  ensureFooter(root) {
    var footer = root.querySelector('.rebuy-cart__flyout-footer');
    if (!footer) return;

    // Rebuy appends its own "(3 items)" to the subtotal label; the count already
    // sits in the header, so the label is reduced back to the bare word.
    var label = footer.querySelector('.rebuy-cart__flyout-subtotal-label span');
    if (label && this.strings.subtotal && label.textContent.indexOf('(') !== -1) {
      label.textContent = this.strings.subtotal;
    }

    if (this.strings.taxesNote && !footer.querySelector('.fs-rebuy-cart-disclaimer')) {
      var note = document.createElement('p');
      note.className = 'fs-rebuy-cart-disclaimer';
      note.textContent = this.strings.taxesNote;
      footer.appendChild(note);
    }
  }

  enhance() {
    var root = this.flyout();
    if (!root) return;

    this.ensureWave(root);
    this.ensureHeader(root);
    this.updateCount(root);
    this.syncEmptyState(root);
    this.ensureProgress(root);
    this.updateProgress(root);
    this.ensureItemReviews(root);
    this.moveGiftNote(root);
    this.cleanGiftNote(root);
    this.ensureFooter(root);
  }

  schedule() {
    if (this._scheduled) return;
    this._scheduled = true;
    var self = this;
    requestAnimationFrame(function () {
      self._scheduled = false;
      self.enhance();
    });
  }
}

if (!customElements.get('rebuy-cart-enhancer')) {
  customElements.define('rebuy-cart-enhancer', RebuyCartEnhancer);
}
